import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import useFrenchField from './frenchField';
import './SubtitlePanel.css';

/**
 * SubtitleAddForm — add a subtitle for the Add panel's time window (shared
 * with the word annotation form). French is optional ("Include French
 * text"); English is required.
 *
 * Props:
 *   videoId
 *   start, end — the window, in seconds (end null: until the next subtitle)
 *   onAdded(subtitle)
 */
function SubtitleAddForm({ videoId, start, end, onAdded }) {
  const [showFrench, setShowFrench] = useFrenchField();
  const [originalText, setOriginalText] = useState('');
  const [englishText, setEnglishText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canSave = (!showFrench || originalText.trim()) && englishText.trim() && (end === null || end >= start) && !saving;

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const subtitle = {
        videoId,
        startTime: start,
        endTime: end,
        originalText: originalText.trim(),
        englishText: englishText.trim(),
        createdAt: serverTimestamp(),
        createdBy: 'annotator',
      };
      const ref = await addDoc(collection(db, 'subtitles'), subtitle);
      setOriginalText('');
      setEnglishText('');
      onAdded && onAdded({ id: ref.id, ...subtitle });
    } catch (err) {
      console.error('Error saving subtitle:', err);
      setError('Failed to save. Please try again.');
    }
    setSaving(false);
  };

  return (
    <form className="subtitle-add-form" onSubmit={handleAdd}>
      <label className="subtitle-form-toggle" title="Untick when the video already has French captions on YouTube">
        <input type="checkbox" checked={showFrench} onChange={(e) => setShowFrench(e.target.checked)} />
        Include French text
      </label>

      {showFrench && (
        <div className="subtitle-form-field">
          <label className="subtitle-form-label" htmlFor="add-french">French text *</label>
          <input id="add-french" className="subtitle-form-input" type="text" placeholder="Subtitle in French"
            value={originalText} onChange={(e) => setOriginalText(e.target.value)} aria-label="French subtitle text" />
        </div>
      )}

      <div className="subtitle-form-field">
        <label className="subtitle-form-label" htmlFor="add-english">English text *</label>
        <input id="add-english" className="subtitle-form-input" type="text" placeholder="English translation"
          value={englishText} onChange={(e) => setEnglishText(e.target.value)} aria-label="English translation" />
      </div>

      {error && <p className="subtitle-form-error">{error}</p>}

      <div className="subtitle-form-actions">
        <button type="submit" className="subtitle-form-save-btn" disabled={!canSave}>
          {saving ? 'Saving…' : 'Add subtitle'}
        </button>
      </div>
    </form>
  );
}

export default SubtitleAddForm;
